import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Flag, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { flagMatch } from '@/lib/api';
import { toast } from 'sonner';

const REASON_MAX = 140;

export default function FlagMatchButton({
  matchId,
  size = 'sm',
  variant = 'outline',
  label = 'Flag for Investigation',
  className = '',
}) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [flagged, setFlagged] = useState(false);

  if (!isAuthenticated) {
    return (
      <Link to="/login">
        <Button
          size={size}
          variant={variant}
          className={className}
          data-testid="flag-match-button-locked"
        >
          <Flag className="w-3.5 h-3.5 mr-1.5" />
          Sign in to flag
        </Button>
      </Link>
    );
  }

  if (flagged) {
    return (
      <Button
        size={size}
        variant="ghost"
        disabled
        className={`text-emerald-600 ${className}`}
        data-testid="flag-match-button-done"
      >
        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
        Flagged
      </Button>
    );
  }

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error('Please share a quick reason.');
      return;
    }
    setSubmitting(true);
    try {
      await flagMatch(matchId, trimmed);
      setFlagged(true);
      setOpen(false);
      setReason('');
      toast.success('Flag submitted for our team to review.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not flag this match.');
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = REASON_MAX - reason.length;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={className}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid="flag-match-button"
      >
        <Flag className="w-3.5 h-3.5 mr-1.5" />
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="flag-match-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-amber-500" />
              Flag this match for investigation
            </DialogTitle>
            <DialogDescription className="text-xs">
              Our team reviews every flag personally. This does not send any
              outreach — it just queues the match for human review.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
              placeholder="Why does this look like the same person? (140 chars max)"
              maxLength={REASON_MAX}
              rows={3}
              className="text-sm"
              data-testid="flag-match-reason-input"
            />
            <div className="flex items-center justify-between">
              <span
                className={`text-xs ${
                  remaining < 20 ? 'text-amber-600' : 'text-muted-foreground'
                }`}
              >
                {remaining} characters left
              </span>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting || !reason.trim()}
                data-testid="flag-match-submit"
              >
                {submitting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Flag className="w-3.5 h-3.5 mr-1.5" />
                )}
                Submit flag
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
